import React, { useMemo } from "react";
import { LOCAL_ICONS, getTypeColor, getLogIcon, getSentimentIcon } from "./counselingUiUtils";
import { MessageSquare, Clock, User } from "lucide-react";

export default function CounselingRecentLogsContent({
    visitLogs = [],
    students = [],
    teachers = [],
    onStudentClick,
    onLogClick,
    theme
}) {
    // 최근 20개의 심방 기록 추출
    const recentLogs = useMemo(() => {
        const allPeople = [...students, ...teachers];
        return [...visitLogs]
            .filter(log => !!log.date)
            .sort((a, b) => {
                // 날짜가 같으면 createdAt 순으로 정렬
                const dateCompare = new Date(b.date) - new Date(a.date);
                if (dateCompare !== 0) return dateCompare;
                return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            })
            .slice(0, 20)
            .map(log => {
                const person = allPeople.find(p => p.id === log.studentId);
                return {
                    ...log,
                    personName: person ? person.name : (log.studentName || "알 수 없음"),
                    personDept: person ? (person.grade || person.department) : (log.department || ""),
                    personPhoto: person ? person.photoUrl : null,
                    personGender: person ? person.gender : "남"
                };
            });
    }, [visitLogs, students, teachers]);

    if (recentLogs.length === 0) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400/60 p-10">
                <img src={LOCAL_ICONS.memo} width={80} className="opacity-30 mb-4 grayscale" alt="empty" />
                <p className="text-lg font-black font-do-hyeon">아직 기록된 심방이 없습니다.</p>
                <p className="text-sm font-bold opacity-70 mt-1 text-center">선생님들의 따뜻한 기록을 기다리고 있어요! ✨</p>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col min-h-0">
            <div className="px-8 pt-8 pb-4 flex items-center justify-between">
                <div>
                    <h3 className="text-2xl font-black text-gray-800 font-do-hyeon flex items-center gap-2">
                        <Clock className="text-[#354F38]" size={24} /> 최근 심방 타임라인
                    </h3>
                    <p className="text-gray-400 text-sm font-bold mt-1">우리 공동체에 어떤 사랑의 나눔이 있었을까요?</p>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar px-8 pb-10 space-y-6 mt-4">
                {recentLogs.map((log, index) => {
                    const colors = getTypeColor(log.type);
                    return (
                        <div
                            key={log.id}
                            onClick={() => onLogClick(log)}
                            className="group relative flex gap-6"
                        >
                            {/* Timeline Line & Dot */}
                            <div className="flex flex-col items-center">
                                <div className={`w-12 h-12 rounded-2xl bg-white border border-gray-100 shadow-sm z-10 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 ${index === 0 ? 'ring-4 ring-emerald-50' : ''}`}>
                                    <img src={getLogIcon(log.type)} width={24} height={24} alt="icon" className="drop-shadow-sm" />
                                </div>
                                {index !== recentLogs.length - 1 && (
                                    <div className="w-0.5 flex-1 bg-gradient-to-b from-gray-100 via-gray-100 to-transparent mt-2"></div>
                                )}
                            </div>

                            {/* Content Card */}
                            <div className={`flex-1 p-5 rounded-[2rem] border border-gray-100/60 shadow-[0_4px_20px_rgba(0,0,0,0.02)] transition-all duration-300 hover:shadow-[0_15px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 bg-white relative overflow-hidden`}>
                                {/* Gradient Background Accent */}
                                <div className={`absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16 rounded-full opacity-[0.03] transition-transform duration-700 group-hover:scale-150 ${colors.bg}`} />

                                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onStudentClick(log.studentId);
                                        }}
                                        className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded-2xl transition-all"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-gray-100 border border-gray-200 overflow-hidden flex-shrink-0">
                                            {log.personPhoto ? (
                                                <img src={log.personPhoto} className="w-full h-full object-cover" alt="" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-white">
                                                    <img
                                                        src={log.personGender === "남" ? "/images/boy_3d_light.png" : "/images/girl_3d_light.png"}
                                                        className="w-full h-full object-cover scale-110"
                                                        alt="avatar"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-base font-black text-gray-800">{log.personName}</span>
                                                <span className="text-[10px] font-black px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-400 uppercase tracking-tighter">
                                                    {log.personDept}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 mt-0.5">
                                                <Clock size={10} /> {log.date}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <div className={`px-3 py-1 rounded-full text-[11px] font-black shadow-sm ${colors.bg} ${colors.text} flex items-center gap-1.5`}>
                                            <MessageSquare size={10} /> {log.type}
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-white/50 border border-white flex items-center justify-center shadow-sm">
                                            {getSentimentIcon(log.sentiment)}
                                        </div>
                                    </div>
                                </div>

                                <p className="text-gray-700 leading-relaxed font-bold text-sm whitespace-pre-wrap pl-1 mb-4">
                                    {log.content}
                                </p>

                                {log.createdByName && (
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                                        <div className="flex items-center gap-2 text-[11px] text-gray-400 font-bold bg-gray-50 px-2.5 py-1 rounded-full">
                                            <User size={10} />
                                            <span>{log.createdByName} 선생님</span>
                                        </div>
                                        <span className="text-[10px] font-bold text-gray-300">
                                            작성일: {log.createdAt ? new Date(log.createdAt).toLocaleDateString() : log.date}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
